import React, { Component } from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Button,
  TextField,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  InputLabel,
  Select
} from '@material-ui/core';
import { NumberFormatField } from '../helpers/NumberFormatField';
import { isValidEmail, isValidURL } from '../../utils/validations';
import { TAX_ID_LENGTH, DUNS_NUMBER_LENGTH, NAICS_CODE_LENGTH } from '../../utils/structures';
import { IBusinessBioState, IBusinessInfoState } from '../../types/CompanyBio.types';
import { ILanguageStrings } from '../../utils/languages/languages';
import { QUESTIONID, ANNUAL_SALE } from '../../types/CompanyConfig.types';

interface CompanyBioProps {
  nextStep: Function;
  previousStep: Function;
  handleChange: Function;
  dataBusinessBio: IBusinessBioState;
  dataBusinessInfo: IBusinessInfoState;
  isAlreadyTagged?: boolean;
  taggedDate?: string;
  languageStrings: ILanguageStrings;
  requiredFields: number[];
}

const NumberFormatTaxId = (props: Object) => <NumberFormatField format="##-#######" {...props} />;
const NumberFormatDUNS = (props: Object) => <NumberFormatField format="##-###-####" {...props} />;
const NumberFormatNAICS = (props: Object) => <NumberFormatField format="######" {...props} />;
const NumberFormatRevenue = (props: Object) => <NumberFormatField prefix="$" thousandSeparator {...props} />;

export class CompanyBio extends Component<CompanyBioProps, { emailValidation: boolean; isUrlValid: boolean }> {
  constructor(props: CompanyBioProps) {
    super(props);

    let emailValidation = false;
    let isUrlValid = false;

    const { companyEmailAddress, companyWebsite } = props.dataBusinessInfo;

    if (typeof companyEmailAddress === 'string' && companyEmailAddress.trim().length > 0) {
      emailValidation = isValidEmail(companyEmailAddress);
    }

    if (typeof companyWebsite === 'string' && companyWebsite.trim().length > 0) {
      isUrlValid = isValidURL(companyWebsite);
    }

    this.state = {
      emailValidation,
      isUrlValid
    };
  }

  componentDidUpdate(prevProps: CompanyBioProps) {
    const { companyEmailAddress, companyWebsite } = this.props.dataBusinessInfo;
    const prevEmail = prevProps.dataBusinessInfo.companyEmailAddress;
    const prevWebsite = prevProps.dataBusinessInfo.companyWebsite;

    if (typeof companyEmailAddress === 'string' && companyEmailAddress.trim().length > 0) {
      const emailValidation = isValidEmail(companyEmailAddress);
      // Only trigger if validation state differs AND the prop value actually changed
      if(emailValidation !== this.state.emailValidation && companyEmailAddress !== prevEmail) {
        this.handleEmailChange({target: {value: companyEmailAddress}}, 'companyEmailAddress', 'businessInfo');
      }
    }

    if (typeof companyWebsite === 'string' && companyWebsite.trim().length > 0) {
      const isUrlValid = isValidURL(companyWebsite);
      // Only trigger if validation state differs AND the prop value actually changed
      if(isUrlValid !== this.state.isUrlValid && companyWebsite !== prevWebsite) {
        this.handleWebsiteChange({target: {value: companyWebsite}}, 'companyWebsite', 'businessInfo');
      }
    }
  }

  shouldShrinkLabel(value: any) {
    if (value) {
      return true;
    }
    return false;
  }

  nextStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.nextStep();
  };

  previousStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.previousStep();
  };

  handleEmailChange = (
    event: { target: { value: string } },
    field: string,
    formStateName: 'businessBio' | 'businessInfo'
  ) => {
    const email = event.target.value.toLowerCase();
    const emailValidation = isValidEmail(email);
    this.setState({
      emailValidation
    });

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: email
      }
    };

    this.props.handleChange(formStateName, field)(newEvent);
  };

  handleWebsiteChange = (
    event: { target: { value: string } },
    field: string,
    formStateName: 'businessBio' | 'businessInfo'
  ) => {
    const website = event.target.value;
    const isUrlValid = isValidURL(website);
    this.setState({
      isUrlValid
    });

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: website
      }
    };

    this.props.handleChange(formStateName, field)(newEvent);
  };

  handleChange = (
    event: { target: { value: string } },
    field: string,
    formStateName: 'businessBio' | 'businessInfo'
  ) => {
    this.props.handleChange(formStateName, field)(event);
  };

  handleChangeOwnership = (e: any) => {
    this.handleChange(e, 'supplierCompanyOwnership', 'businessBio');
  };

  getFilteredRequiredFields = () => {
    /*
      If there's *any* annual sale field marked as required, we'll have the last
      3 years' annual sale fields be required; every other annual sale field will be ignored.
    */
    var hasAnyAnnualSaleField = this.props.requiredFields.some(requiredField => Object.values(ANNUAL_SALE).includes(requiredField));
    if (!hasAnyAnnualSaleField) {
      return this.props.requiredFields;
    }

    // We're removing all annual sale fields and adding back just the fields for the last N years
    var requiredFieldsWithoutAnnualSale = this.props.requiredFields.filter(requiredField => !Object.values(ANNUAL_SALE).includes(requiredField));
    return requiredFieldsWithoutAnnualSale.concat(this.getRenderedAnnualSalesIds());
  }

  configRequiredFields = () => {
    return this.getFilteredRequiredFields().some(requiredField => {
      const reqField: HTMLElement | null = document.getElementById(requiredField.toString());

      if (!reqField) {
        return true; // Field not found, treat as invalid
      }

      // Check if this is a RadioGroup (will be a fieldset or div with role="radiogroup")
      if (reqField.getAttribute('role') === 'radiogroup' || reqField.tagName === 'FIELDSET') {
        const checkedRadio = reqField.querySelector('input[type="radio"]:checked') as HTMLInputElement;
        return !checkedRadio || checkedRadio.value.trim().length === 0;
      }

      // For regular input fields (TextField, Select, etc.)
      const inputField = reqField as HTMLInputElement;
      return !inputField || inputField.value.trim().length === 0;
    });
  };

  getRenderedAnnualSalesIds = (yearsBack: number = 3) => {
    const date = new Date();
    const currentYear = date.getFullYear();
    return Array.from({ length: yearsBack }, (_, i) => ANNUAL_SALE[currentYear - 1 - i]);
  }

  isContinueDisabled = () => {
    this.configRequiredFields();
    const { yearCompanyWasFounded, numberOfPermanentEmployees, supplierCompanyOwnership } = this.props.dataBusinessBio;
    let yearCompany = typeof yearCompanyWasFounded === 'string' ? yearCompanyWasFounded : `${yearCompanyWasFounded}`;
    let employees =
      typeof numberOfPermanentEmployees === 'string' ? numberOfPermanentEmployees : `${numberOfPermanentEmployees}`;

    return (
      yearCompany.trim().length === 0 ||
      employees.trim().length === 0 ||
      supplierCompanyOwnership.trim().length === 0 ||
      this.hasFormErrors() ||
      this.configRequiredFields()
    );
  };

  /**
   * Field Validations
   */

  hasCompanyTaxIdError = () => {
    const length = this.props.dataBusinessInfo.companyTaxIdNumber.trim().length;
    return length > 0 && length < TAX_ID_LENGTH;
  };

  hasCompanyDUNSNumberError = () => {
    const length = this.props.dataBusinessInfo.dunsNumber.trim().length; 
    return length > 0 && length < DUNS_NUMBER_LENGTH;
  };

  hasCompanyNAICSCodeError = (field: string) => {
    const length = field.trim().length;
    return length > 0 && length < NAICS_CODE_LENGTH;
  };

  hasFormErrors = () => {
    const { primaryNAICSCode, secondaryNAICSCode, companyEmailAddress, companyWebsite } = this.props.dataBusinessInfo;
    const { emailValidation, isUrlValid } = this.state;
    const invalidEmail = companyEmailAddress.length ? !emailValidation : false;
    const invalidUrl = companyWebsite.length ? !isUrlValid : false;
    return (
      this.hasCompanyTaxIdError() ||
      this.hasCompanyDUNSNumberError() ||
      this.hasCompanyNAICSCodeError(primaryNAICSCode) ||
      this.hasCompanyNAICSCodeError(secondaryNAICSCode) ||
      invalidEmail ||
      invalidUrl
    );
  };

  renderAnnualRevenue = () => {
    const date = new Date();
    const currentYear = date.getFullYear();

    const lastYearId = this.getRenderedAnnualSalesIds()[0];
    const secondToLastYearId = this.getRenderedAnnualSalesIds()[1];
    const thirdToLastYearId = this.getRenderedAnnualSalesIds()[2];

    // If ANY annual sale field is required, then all 3 recent years are required
    const annualSalesRequired = this.props.requiredFields.some(requiredField => 
      Object.values(ANNUAL_SALE).includes(requiredField)
    );

    return (
        <>
          <Grid item sm={4} style={{ paddingTop: 0 }}>
            <TextField
                required={annualSalesRequired}
                key={`annualSale${currentYear - 1}`}
                id={lastYearId.toString()}
                name={`annualSale${currentYear - 1}`}
                label={this.props.languageStrings.formatString(this.props.languageStrings.companyBio.annualRevenueLabel, {
                  newAnnualRevenue: currentYear - 1
                })}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'annualSale1', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.annualSale1) }}
                InputProps={{
                  inputComponent: NumberFormatRevenue
                }}
                value={this.props.dataBusinessInfo.annualSale1}
            />
          </Grid>

          <Grid item sm={4} style={{ paddingTop: 0 }}>
            <TextField
                required={annualSalesRequired}
                key={`annualSale${currentYear - 2}`}
                id={secondToLastYearId.toString()}
                name={`annualSale${currentYear - 2}`}
                label={this.props.languageStrings.formatString(this.props.languageStrings.companyBio.annualRevenueLabel, {
                  newAnnualRevenue: currentYear - 2
                })}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'annualSale2', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.annualSale2) }}
                InputProps={{
                  inputComponent: NumberFormatRevenue
                }}
                value={this.props.dataBusinessInfo.annualSale2}
            />
          </Grid>

          <Grid item sm={4} style={{ paddingTop: 0 }}>
            <TextField
                required={annualSalesRequired}
                key={`annualSale${currentYear - 3}`}
                id={thirdToLastYearId.toString()}
                name={`annualSale${currentYear - 3}`}
                label={this.props.languageStrings.formatString(this.props.languageStrings.companyBio.annualRevenueLabel, {
                  newAnnualRevenue: currentYear - 3
                })}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'annualSale3', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.annualSale3) }}
                InputProps={{
                  inputComponent: NumberFormatRevenue
                }}
                value={this.props.dataBusinessInfo.annualSale3}
            />
          </Grid>
        </>
    );
  };

  renderFormControls = () => {
    return (
      <div className="location-controls">
        <div className="location-wrapper">
          <Grid container spacing={3} style={{ width: '100%' }}>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyLegalName)}
                id={QUESTIONID.companyLegalName.toString()}
                name={QUESTIONID.companyLegalName.toString()}
                label={this.props.languageStrings.companyBio.companyLegalName}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'companyLegalName', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyLegalName}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyDoingBusinessAs)}
                id={QUESTIONID.companyDoingBusinessAs.toString()}
                name={QUESTIONID.companyDoingBusinessAs.toString()}
                label={this.props.languageStrings.companyBio.companyDoingBusinessAs}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'companyDoingBusinessAs', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyDoingBusinessAs}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyParentCompany)}
                id={QUESTIONID.companyParentCompany.toString()}
                name={QUESTIONID.companyParentCompany.toString()}
                label={this.props.languageStrings.companyBio.companyParentCompany}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'companyParentCompany', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyParentCompany}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required
                id={QUESTIONID.yearCompanyWasFounded.toString()}
                name={QUESTIONID.yearCompanyWasFounded.toString()}
                label={this.props.languageStrings.companyBio.yearCompanyWasFounded}
                fullWidth
                variant="outlined"
                type="number"
                onChange={e => this.handleChange(e, 'yearCompanyWasFounded', 'businessBio')}
                value={this.props.dataBusinessBio.yearCompanyWasFounded}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required
                id={QUESTIONID.numberOfPermanentEmployees.toString()}
                name={QUESTIONID.numberOfPermanentEmployees.toString()}
                label={this.props.languageStrings.companyBio.numberOfPermanentEmployees}
                fullWidth
                variant="outlined"
                type="number"
                onChange={e => this.handleChange(e, 'numberOfPermanentEmployees', 'businessBio')}
                value={this.props.dataBusinessBio.numberOfPermanentEmployees}
              />
            </Grid>
            <Grid item sm={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>{this.props.languageStrings.companyBio.supplierCompanyOwnership}</InputLabel>
                <Select
                  native
                  required
                  fullWidth
                  data-testid="supplierCompanyOwnership"
                  onChange={e => this.handleChangeOwnership(e)}
                  inputProps={{
                    id: QUESTIONID.supplierCompanyOwnership.toString(),
                    name: QUESTIONID.supplierCompanyOwnership.toString()
                  }}
                  value={this.props.dataBusinessBio.supplierCompanyOwnership}
                  label={this.props.languageStrings.companyBio.supplierCompanyOwnership}
                >
                  {this.props.languageStrings.companyBio.supplierCompanyOwnershipOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item sm={12} style={{ paddingBottom: 0 }}>
              <FormControl component="fieldset" classes={{ root: 'custom-fieldset' }}>
                <FormLabel component="label">{this.props.languageStrings.companyBio.unionizedCompany}</FormLabel>
                <RadioGroup
                  aria-label="unionizedCompany"
                  id={QUESTIONID.unionizedCompany.toString()}
                  name={QUESTIONID.unionizedCompany.toString()}
                  value={this.props.dataBusinessBio.unionizedCompany.toLowerCase()}
                  onChange={e => this.handleChange(e, 'unionizedCompany', 'businessBio')}
                  row
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={this.props.languageStrings.affirmativeLabel}
                    style={{ marginLeft: '5px' }}
                  />
                  <FormControlLabel value="no" control={<Radio />} label={this.props.languageStrings.negativeLabel} />
                </RadioGroup>
              </FormControl>
            </Grid>
            {this.renderAnnualRevenue()}
            <Grid item sm={4}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyPhoneNumber)}
                id={QUESTIONID.companyPhoneNumber.toString()}
                name={QUESTIONID.companyPhoneNumber.toString()}
                label={this.props.languageStrings.companyBio.companyPhoneNumber}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'companyPhoneNumber', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyPhoneNumber}
              />
            </Grid>
            <Grid item sm={4}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyWebsite)}
                id={QUESTIONID.companyWebsite.toString()}
                name={QUESTIONID.companyWebsite.toString()}
                label={this.props.languageStrings.companyBio.companyWebsite}
                fullWidth
                variant="outlined"
                error={this.state.isUrlValid === false && this.props.dataBusinessInfo.companyWebsite.length > 0}
                helperText={
                  this.state.isUrlValid === false && this.props.dataBusinessInfo.companyWebsite.length > 0
                    ? this.props.languageStrings.companyBio.errorCompanyWebsite
                    : ''
                }
                onChange={e => this.handleWebsiteChange(e, 'companyWebsite', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyWebsite}
              />
            </Grid>
            <Grid item sm={4}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyEmailAddress)}
                id={QUESTIONID.companyEmailAddress.toString()}
                name={QUESTIONID.companyEmailAddress.toString()}
                label={this.props.languageStrings.companyBio.companyEmailAddress}
                fullWidth
                variant="outlined"
                type="email"
                error={
                  this.state.emailValidation === false && this.props.dataBusinessInfo.companyEmailAddress.length > 0
                }
                helperText={
                  this.state.emailValidation === false && this.props.dataBusinessInfo.companyEmailAddress.length > 0
                    ? this.props.languageStrings.companyBio.errorEmailAddress
                    : ''
                }
                onChange={e => this.handleEmailChange(e, 'companyEmailAddress', 'businessInfo')}
                value={this.props.dataBusinessInfo.companyEmailAddress}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.companyTaxIdNumber)}
                id={QUESTIONID.companyTaxIdNumber.toString()}
                name={QUESTIONID.companyTaxIdNumber.toString()}
                label={this.props.languageStrings.companyBio.companyTaxIdNumber}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'companyTaxIdNumber', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.companyTaxIdNumber) }}
                InputProps={{
                  inputComponent: NumberFormatTaxId
                }}
                error={this.hasCompanyTaxIdError()}
                helperText={this.hasCompanyTaxIdError() ? this.props.languageStrings.companyBio.errorTaxIdNumber : ''}
                value={this.props.dataBusinessInfo.companyTaxIdNumber}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.dunsNumber)}
                id={QUESTIONID.dunsNumber.toString()}
                name={QUESTIONID.dunsNumber.toString()}
                label={this.props.languageStrings.companyBio.dunsNumber}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'dunsNumber', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.dunsNumber) }}
                InputProps={{
                  inputComponent: NumberFormatDUNS
                }}
                error={this.hasCompanyDUNSNumberError()}
                helperText={
                  this.hasCompanyDUNSNumberError() ? this.props.languageStrings.companyBio.errorDunsNumber : ''
                }
                value={this.props.dataBusinessInfo.dunsNumber}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.primaryNAICSCode)}
                id={QUESTIONID.primaryNAICSCode.toString()}
                name={QUESTIONID.primaryNAICSCode.toString()}
                label={this.props.languageStrings.companyBio.primaryNAICSCode}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'primaryNAICSCode', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.primaryNAICSCode) }}
                InputProps={{
                  inputComponent: NumberFormatNAICS
                }}
                error={this.hasCompanyNAICSCodeError(this.props.dataBusinessInfo.primaryNAICSCode)}
                helperText={
                  this.hasCompanyNAICSCodeError(this.props.dataBusinessInfo.primaryNAICSCode)
                    ? this.props.languageStrings.companyBio.errorNAICSCode
                    : ''
                }
                value={this.props.dataBusinessInfo.primaryNAICSCode}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                required={this.props.requiredFields.includes(QUESTIONID.secondaryNAICSCode)}
                id={QUESTIONID.secondaryNAICSCode.toString()}
                name={QUESTIONID.secondaryNAICSCode.toString()}
                label={this.props.languageStrings.companyBio.secondaryNAICSCode}
                fullWidth
                variant="outlined"
                onChange={e => this.handleChange(e, 'secondaryNAICSCode', 'businessInfo')}
                InputLabelProps={{ shrink: this.shouldShrinkLabel(this.props.dataBusinessInfo.secondaryNAICSCode) }}
                InputProps={{
                  inputComponent: NumberFormatNAICS
                }}
                error={this.hasCompanyNAICSCodeError(this.props.dataBusinessInfo.secondaryNAICSCode)}
                helperText={
                  this.hasCompanyNAICSCodeError(this.props.dataBusinessInfo.secondaryNAICSCode)
                    ? this.props.languageStrings.companyBio.errorNAICSCode
                    : ''
                }
                value={this.props.dataBusinessInfo.secondaryNAICSCode}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    );
  };

  renderTaggedMessage(taggedDate: string = '') {
    if (taggedDate && taggedDate.trim().length > 0) {
      return (
        <div className="taggedMessage" style={{ width: '60%', margin: '0 auto' }}>
          <Alert severity="info">
            {this.props.languageStrings.formatString(this.props.languageStrings.common.tagInstructionPrimary, {
              taggedDate
            })}
            .&nbsp;
            <strong>
              {this.props.languageStrings.common.tagInstructionSecondary}&nbsp;
              <span>{this.props.languageStrings.continueButtonLabel}.</span>
            </strong>
          </Alert>
        </div>
      );
    } else {
      return '';
    }
  }

  render() {
    return (
      <div className="CompanySelection">
        <div className="Wrapper">
          {this.renderTaggedMessage(this.props.taggedDate)}
          {this.renderFormControls()}

          <div className="Form-actions multiple">
            <Button
              variant="outlined"
              color="primary"
              onClick={e => {
                this.previousStep(e);
              }}
            >
              {this.props.languageStrings.backButtonLabel}
            </Button>

            <Button
              disabled={this.isContinueDisabled()}
              variant="contained"
              color="primary"
              onClick={e => {
                this.nextStep(e);
              }}
            >
              {this.props.languageStrings.continueButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default CompanyBio;
